import {
  Center,
  Loader,
  NativeSelect,
  useMantineColorScheme,
} from "@mantine/core";
import { safeApiCall } from "api/common.service";
import { getStaffUsers } from "api/user.service";
import React, { useEffect, useState } from "react";
import { SalesPeople, SalesPerson } from "types/model.types";

type SalesPersonSelectionProps = {
  value: string;
  onChange: (value: string) => void;

  label?: string;
  readonlyMode?: boolean;
  placeHolderText?: string;
  disabled?: boolean;
  styles?: React.CSSProperties;
};

const SalesPersonSelection: React.FC<SalesPersonSelectionProps> = ({
  value,
  onChange,
  readonlyMode = false,
  label = "Sales Person",
  placeHolderText = "Select a sales person",
  disabled = false,
  styles,
  ...rest
}) => {
  const [salesPeople, setSalesPeople] = useState<SalesPeople>([]);
  const [loading, setLoading] = useState(true);

  const { colorScheme } = useMantineColorScheme();
  const isDark = colorScheme === "dark";
  const labelColor = isDark ? "#e0e0e0" : "#1a1a1a";

  // Fetch staff users once
  useEffect(() => {
    let mounted = true;

    const fetchUsers = async () => {
      const users = await safeApiCall(getStaffUsers());

      const mapped = users
        .map((u: { userName: string }) => ({
          value: u.userName,
          label: formatLabel(u.userName),
        }))
        .sort((a: SalesPerson, b: SalesPerson) =>
          a.label.localeCompare(b.label),
        );

      if (!mounted) return;

      setSalesPeople(mapped);
      setLoading(false);
    };

    fetchUsers();
    return () => {
      mounted = false;
    };
  }, []);

  function formatLabel(email: string): string {
    const namePart = email.split("@")[0];
    return namePart.replace(/\./g, " ");
  }

  if (loading)
    return (
      <Center style={{ height: "100vh" }}>
        <Loader size="lg" />
      </Center>
    );

  return (
    <NativeSelect
      label={label}
      value={value ?? ""}
      onChange={(e) => onChange(e.currentTarget.value)}
      disabled={readonlyMode}
      data={salesPeople}
      styles={{
        input: {
          backgroundColor: readonlyMode
            ? isDark
              ? "#2a2a2a"
              : "#f5f5f5"
            : isDark
              ? "#3a3a3a"
              : "white",

          borderColor: readonlyMode
            ? isDark
              ? "#555"
              : "#d0d0d0"
            : isDark
              ? "#4dabf7"
              : "#228be6",
          color: readonlyMode
            ? isDark
              ? "#c1c1c1"
              : "#1a1a1a" // <-- added this
            : isDark
              ? "#fff"
              : "#000",

          cursor: readonlyMode ? "default" : "pointer",
          transition: "background-color 120ms ease, border-color 120ms ease",
        },
        label: {
          fontWeight: 600,
          fontSize: "lg",
          color: labelColor,
        },
      }}
      {...rest}
    />
  );
};

export default SalesPersonSelection;
